import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {TRANSLOCO_SCOPE} from '@ngneat/transloco';
import {scopeLoader} from '@shared/i18n/transloco-scoped-loader.helper.';
import {AuthenticationService} from '@shared/services';
import {TwoFactorAuthenticationMethodsEnum} from '@shared/models/auth.models';
import {NgOtpInputComponent} from 'ng-otp-input';

@Component({
  selector: 'app-two-fa-verify',
  templateUrl: './two-fa-verify.component.html',
  styleUrls: ['./two-fa-verify.component.scss'],
  providers:
    [{
      provide: TRANSLOCO_SCOPE,
      useValue: {scope: 'twoFaVerify', loader: scopeLoader((lang, root) => import(`./${root}/${lang}.json`))}
    }]
})
export class TwoFaVerifyComponent implements OnInit, OnChanges, OnDestroy {
  config = {
    allowNumbersOnly: true,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: '',
    inputClass: 'form-control d-inline',
    inputStyles: {
      'font-size': '1.2rem',
      width: '50px',
      height: '50px'
    }
  };
  @Input() loading = false;
  @Input() hideLogout = false;
  @Input() hideTitle = false;
  @Input() otpCooldownTime: string;
  @Input() availableProviders: TwoFactorAuthenticationMethodsEnum[] = [];
  @Input() defaultProvider: TwoFactorAuthenticationMethodsEnum;

  otp = '';
  rememberBrowser = false;
  timerActive = false;
  showMethodSelector = false;
  selectedMethod: TwoFactorAuthenticationMethodsEnum;
  readonly TwoFactorMethod = TwoFactorAuthenticationMethodsEnum;

  @ViewChild('timer') timerRef: ElementRef;
  @ViewChild(NgOtpInputComponent, {static: false}) otpInputComponent: NgOtpInputComponent;

  private intervalId: ReturnType<typeof setInterval> | null = null;

  @Output() verify: EventEmitter<{ code: string, rememberBrowser: boolean }> = new EventEmitter<{ code: string, rememberBrowser: boolean }>();
  @Output() logout = new EventEmitter<void>();
  @Output() resendOtp = new EventEmitter<TwoFactorAuthenticationMethodsEnum | undefined>();

  constructor(
    private authenticationService: AuthenticationService,
    private ngZone: NgZone,
    private renderer: Renderer2
  ) {
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.otpCooldownTime?.currentValue) {
      this.startTimer();
    }
    if (changes.defaultProvider?.currentValue != null && this.selectedMethod == null) {
      this.selectedMethod = this.defaultProvider;
    }
  }

  ngOnDestroy(): void {
    if (this.intervalId != null) clearInterval(this.intervalId);
  }

  private startTimer(): void {
    const expiry = new Date(this.otpCooldownTime);
    if (isNaN(expiry.getTime())) return;
    if (this.intervalId != null) clearInterval(this.intervalId);
    this.timerActive = true;
    this.showMethodSelector = false;
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => {
        const remaining = Math.ceil((expiry.getTime() - Date.now()) / 1000);
        const el = this.timerRef?.nativeElement;
        if (remaining <= 0) {
          clearInterval(this.intervalId!);
          this.intervalId = null;
          if (el) this.renderer.setProperty(el, 'innerHTML', '');
          this.ngZone.run(() => this.timerActive = false);
          return;
        }
        if (el) {
          this.renderer.setProperty(el, 'innerHTML', this.formatDuration(remaining));
        }
      }, 500);
    });
  }

  private formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  }

  get currentMethodLabel(): string {
    const method = this.selectedMethod ?? this.defaultProvider;
    if (method === TwoFactorAuthenticationMethodsEnum.SMS) return 'SMS';
    if (method === TwoFactorAuthenticationMethodsEnum.Email) return 'Email';
    return 'email address';
  }

  onLogout() {
    this.authenticationService.logout();
    this.logout.emit();
  }

  verifyOtp() {
    this.verify.emit({code: this.otp, rememberBrowser: this.rememberBrowser});
  }

  onOtpChange($event: string) {
    this.otp = $event;
  }

  resend() {
    this.otpInputComponent.setValue('');
    this.resendOtp.emit(this.selectedMethod);
  }

  toggleMethodSelector() {
    this.showMethodSelector = !this.showMethodSelector;
  }

  selectMethod(method: TwoFactorAuthenticationMethodsEnum) {
    this.selectedMethod = method;
  }
}
